import { useState, useEffect } from 'react';
import { useAxios } from '../core/_axios';
import { useAuth } from '../AuthContext';

// Custom event for email follow-up status changes
export const EMAIL_FOLLOW_UP_CHANGED_EVENT = 'emailFollowUpChanged';

export const triggerEmailFollowUpChanged = (enabled: boolean) => {
  window.dispatchEvent(new CustomEvent(EMAIL_FOLLOW_UP_CHANGED_EVENT, { detail: { enabled } }));
};

export const useEmailFollowUpEnabled = () => {
  const [emailFollowUpEnabled, setEmailFollowUpEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUserAccountUniqueId, viewingAllAccounts } = useAuth();
  const axios = useAxios();

  const checkEmailFollowUp = async () => {
    // If viewing all accounts, consider email follow-up as enabled to allow access
    if (viewingAllAccounts) {
      setEmailFollowUpEnabled(true);
      setLoading(false);
      return;
    }

    if (!currentUserAccountUniqueId) {
      setEmailFollowUpEnabled(false);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get('/authenticated/current-account/get-current-account');
      
      if (response.data.success) {
        const account = response.data.data.account;
        const enabled = account.public_data?.email_follow_up_enabled === true;
        const hasEmail = account.email_address && account.email_address.trim() !== '';
        
        // Only consider email follow-up enabled if both conditions are met
        setEmailFollowUpEnabled(enabled && hasEmail);
      } else {
        setEmailFollowUpEnabled(false);
      }
    } catch (error) {
      console.error('Failed to check email follow-up status:', error);
      setEmailFollowUpEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkEmailFollowUp();
  }, [currentUserAccountUniqueId, viewingAllAccounts]);

  // Listen for email follow-up status changes
  useEffect(() => {
    const handleEmailFollowUpChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[useEmailFollowUpEnabled] Email follow-up status changed:', customEvent.detail.enabled);
      // Re-check the status when it changes
      checkEmailFollowUp();
    };

    window.addEventListener(EMAIL_FOLLOW_UP_CHANGED_EVENT, handleEmailFollowUpChanged);
    
    return () => {
      window.removeEventListener(EMAIL_FOLLOW_UP_CHANGED_EVENT, handleEmailFollowUpChanged);
    };
  }, [currentUserAccountUniqueId, viewingAllAccounts]);

  return { emailFollowUpEnabled, loading };
};

export default useEmailFollowUpEnabled;